import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { not_selected: "Not Selected", shortlisted: "⭐ Shortlisted", finalized: "✅ Finalized", performance_ready: "🏆 Performance Ready" };

export default function Stories() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const { data, isLoading } = useQuery({ queryKey: ["stories"], queryFn: async () => (await api.get("/stories")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", story_type: "text", content: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <Loader />;

  const save = async () => {
    if (!form.title) return toast.error("Give your story a title!");
    setBusy(true);
    try {
      let file_id = null, file_url = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = up.data.id; file_url = up.data.url;
      }
      await api.post("/stories", { ...form, file_id, file_url });
      toast.success("🎤 Story saved to your portfolio!");
      setOpen(false); setForm({ title: "", story_type: "text", content: "" }); setFile(null);
      qc.invalidateQueries({ queryKey: ["stories"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="stories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-4xl text-csh-blue">My Stories 🎤</h1>
          <p className="text-muted-foreground">Your personal Story Portfolio.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-story-btn" className="rounded-full gradient-purple text-white hover:scale-105"><Icons.Plus size={18} /> New Story</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">Save a New Story</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="story-title" placeholder="Story title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl border-2 py-6" />
              <Select value={form.story_type} onValueChange={(v) => setForm({ ...form, story_type: v })}>
                <SelectTrigger className="rounded-xl border-2 py-6" data-testid="story-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">✍️ Text Story</SelectItem>
                  <SelectItem value="video">🎥 Video</SelectItem>
                  <SelectItem value="audio">🎤 Audio</SelectItem>
                </SelectContent>
              </Select>
              {form.story_type === "text" ? (
                <Textarea placeholder="Once upon a time..." rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="rounded-2xl border-2" />
              ) : (
                <div>
                  <input ref={fileRef} type="file" className="hidden" accept={form.story_type === "video" ? "video/*" : "audio/*"} onChange={(e) => setFile(e.target.files[0])} />
                  <button onClick={() => fileRef.current?.click()} className="flex w-full flex-col items-center gap-1 rounded-2xl border-2 border-dashed border-csh-purple/40 bg-csh-purple/5 py-6">
                    <Icons.UploadCloud className="text-csh-purple" /> <span className="text-sm font-bold">{file ? file.name : "Upload file"}</span>
                  </button>
                </div>
              )}
              <Button onClick={save} disabled={busy} data-testid="save-story-btn" className="w-full rounded-full gradient-orange py-6 font-bold text-white">{busy ? <Icons.Loader2 className="animate-spin" /> : "Save Story"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {(!data || data.length === 0) ? <EmptyState icon="Mic" title="No stories yet" text="Save your first story to start building your portfolio!" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((st) => (
            <div key={st.id} className="rounded-3xl bg-white p-5 shadow-sm border border-black/5 card-pop" data-testid={`story-${st.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl gradient-orange text-white">
                  {st.story_type === "video" ? <Icons.Video size={20} /> : st.story_type === "audio" ? <Icons.Mic size={20} /> : <Icons.BookOpen size={20} />}
                </div>
                <span className="rounded-full bg-csh-purple/10 px-3 py-1 text-xs font-bold text-csh-purple">{STATUS_LABEL[st.status]}</span>
              </div>
              <h3 className="mt-3 font-head text-xl text-csh-blue">{st.title}</h3>
              {st.content && <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{st.content}</p>}
              {st.file_id && st.story_type === "video" && <video src={fileUrl(st.file_id)} controls className="mt-3 w-full rounded-xl" />}
              {st.file_id && st.story_type === "audio" && <audio src={fileUrl(st.file_id)} controls className="mt-3 w-full" />}
              {st.week && <p className="mt-2 text-xs text-muted-foreground">Week {st.week}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
